/* eslint-disable @typescript-eslint/no-use-before-define */
import { ActionButtonProps } from '@euler/app/components/BottomAction';
import { PageContainerForIpad } from '@euler/app/components/PageContainerForIpad';
import { Typography } from '@euler/app/components/typography';
import { useReportHeaderMetrics } from '@euler/app/flows/report/functions/useReportHeaderMetrics';
import { Center } from '@euler/components/Center';
import { Colors } from '@euler/components/Colors';
import { EmptyView } from '@euler/components/EmptyView';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import {
  useIsMobileLayout,
  useIsTabletLandscapeLayout,
} from '@euler/functions/responsiveness';
import { ReportTabKey } from '@euler/model/viewmodel';
import { FC, ReactElement, ReactNode, memo, useMemo } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  ScrollViewProps,
  View,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Fill } from 'react-slot-fill';
import {
  isReportTabContentEmpty,
  projectionForReportTab,
} from '../functions/util';
import {
  ReportContextProvider,
  ReportScreenProps,
  useReportContext,
} from './ReportContext';
import { ReportScrollView } from './ReportScrollView';

export const ReportContentWrapper: FC<
  {
    tab: ReportTabKey;
    children: ReactNode;
    shouldUseLandscapeLayout?: boolean;
    renderScrollView?: (props: ScrollViewProps) => ReactElement;
  } & ReportScreenProps
> = memo(props => {
  const isMobile = useIsMobileLayout();
  const { headerHeight } = useReportHeaderMetrics();

  const projection = useMemo(
    () => projectionForReportTab(props.tab),
    [props.tab],
  );

  return (
    <ReportContextProvider
      target={props.target}
      projection={projection}
      offsetTop={0}
      suspenseFallback={
        props.standalone ? undefined : (
          <Center
            css={`
              flex: 1;
              padding-top: ${isMobile ? 0 : headerHeight}px;
            `}
          >
            <ActivityIndicator />
          </Center>
        )
      }
    >
      <ContentWrapperImpl {...props} />
    </ReportContextProvider>
  );
});

const ContentWrapperImpl: FC<
  {
    tab: ReportTabKey;
    children: ReactNode;
    shouldUseLandscapeLayout?: boolean;
    renderScrollView?: (props: ScrollViewProps) => ReactElement;
  } & ReportScreenProps
> = memo(
  ({
    target,
    tab,
    title,
    subTitle,
    accessoryText,
    standalone,
    action,
    secondaryAction,
    ipadHeaderStyle,
    ipadFooterStyle,
    onShare,
    shareButtonRef,
    renderScrollView,
    children,
    shouldUseLandscapeLayout,
  }) => {
    const { bottom } = useSafeAreaInsets();
    const { rpx } = useResponsiveContext();
    const isLandscapeLayout = useIsTabletLandscapeLayout();

    if (isLandscapeLayout || shouldUseLandscapeLayout) {
      return (
        <LandscapeContent
          target={target}
          tab={tab}
          title={title}
          subTitle={subTitle}
          accessoryText={accessoryText}
          action={action}
          secondaryAction={secondaryAction}
          standalone={standalone}
          ipadHeaderStyle={ipadHeaderStyle}
          ipadFooterStyle={ipadFooterStyle}
          shareButtonRef={shareButtonRef}
          onShare={onShare}
        >
          {children}
        </LandscapeContent>
      );
    }

    if (standalone) {
      return (
        <ReportScrollView
          target={target}
          tab={tab}
          title={title}
          accessoryText={accessoryText}
          action={action}
          secondaryAction={secondaryAction}
          shareButtonRef={shareButtonRef?.()}
          onShare={onShare}
          renderScrollView={renderScrollView}
        >
          <ContentOrEmpty tab={tab}>{children}</ContentOrEmpty>
        </ReportScrollView>
      );
    }

    const scrollViewProps: ScrollViewProps = {
      contentContainerStyle: {
        flexGrow: 1,
        paddingBottom: standalone ? 0 : bottom + rpx(15),
      },
      children: <ContentOrEmpty tab={tab}>{children}</ContentOrEmpty>,
    };

    if (renderScrollView) {
      return renderScrollView(scrollViewProps);
    }

    return (
      <ScrollView
        contentContainerStyle={{
          flexGrow: 1,
          paddingBottom: bottom + rpx(15),
        }}
      >
        <ContentOrEmpty tab={tab}>{children}</ContentOrEmpty>
      </ScrollView>
    );
  },
);

const LandscapeContent = memo(
  ({
    tab,
    children,
    title,
    subTitle,
    action,
    secondaryAction,
    standalone,
    ipadHeaderStyle,
    ipadFooterStyle,
    shareButtonRef,
    onShare,
  }: { tab: ReportTabKey; children?: ReactNode } & ReportScreenProps) => {
    const { report } = useReportContext();
    const { rpx } = useResponsiveContext();

    const actions = useMemo<ActionButtonProps[]>(() => {
      const res: ActionButtonProps[] = [];
      if (secondaryAction != null) {
        res.push({
          ...secondaryAction,
          bordered: true,
          variant: secondaryAction.variant ?? 'white',
          style: { marginLeft: rpx(16) },
        });
      }
      if (action != null) {
        res.push({
          ...action,
          bordered: true,
          variant: action.variant ?? 'white',
          style: { marginLeft: rpx(16) },
        });
      }
      if (onShare != null) {
        res.push({
          bordered: true,
          text: '分享报告',
          variant: 'white',
          buttonRef: shareButtonRef?.(),
          onPress: () => {
            onShare(tab);
          },
          style: { marginLeft: rpx(16) },
        });
      }
      return res;
    }, [action, onShare, rpx, secondaryAction, shareButtonRef, tab]);

    return (
      <PageContainerForIpad
        title={title ?? '报告详情'}
        subTitle={typeof subTitle === 'function' ? subTitle(report) : subTitle}
        actions={actions}
        hideHeader={!standalone}
        name={`reports/${tab}`}
        headerStyle={ipadHeaderStyle}
        footerStyle={ipadFooterStyle}
      >
        <ContentOrEmpty tab={tab}>{children}</ContentOrEmpty>
        <Fill name={`reports/${tab}/page-container/header/right`}>
          <View
            css={`
              align-items: flex-end;
            `}
          >
            <Typography.Label
              bold
              size={rpx(28)}
              color={'white'}
              css={`
                margin-left: ${rpx(10)}px;
              `}
            >
              {report.licensePlateNo}
            </Typography.Label>
            <Typography.Label
              regular
              size={rpx(16)}
              color={Colors.White4}
              css={`
                margin-left: ${rpx(10)}px;
              `}
            >
              {report.vehicleName}
            </Typography.Label>
          </View>
        </Fill>
      </PageContainerForIpad>
    );
  },
);

const ContentOrEmpty = memo(
  ({ tab, children }: { tab: ReportTabKey; children?: ReactNode }) => {
    const { report, error, refresh } = useReportContext();
    const isEmpty = isReportTabContentEmpty(report, tab);
    if (error) {
      return (
        <EmptyView
          error={error}
          message={__DEV__ ? error.message : '获取报告失败，请稍候重试'}
          onRetry={refresh}
        />
      );
    }
    if (isEmpty) {
      return <EmptyView message="暂无相关报告数据" onRetry={refresh} />;
    }
    return <>{children}</>;
  },
);
